import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SYSTEM_CODE } from '@src/app/core/constants/global-constants.constant';
import { MenuInfo, TodoMenuInfo } from '@src/app/core/models/menu-info';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppConfig, UserProfileService } from 'sigv-library';
import { MenuItemProcessor } from 'src/app/core/services/menu-processor.service';
import { deepCopy } from 'src/app/core/utils/common-functions';

@Injectable()
export class WebsubmissionMenuItemProcessor implements MenuItemProcessor {
  private readonly todoMenuInfos: WebSubmissionTodoMenuInfo[] = [
    {
      icon: 'ic-draft',
      srcLabel: 'Menu.Draft',
      entryUrl: '/todo/draft-list',
      eventCountKey: 'draftCount',
    },
    {
      icon: 'ic-reject',
      srcLabel: 'Menu.Returned/Pending Additional Doc',
      entryUrl: '/todo/returned-pending-additional-doc',
      eventCountKey: 'returnedCount',
    },
    {
      icon: 'ic-wait',
      srcLabel: 'Menu.Pending Commencement Doc',
      entryUrl: 'todo/pending-commencement-doc-list',
      eventCountKey: 'pendingCommencementDocCount',
    },
    {
      icon: 'ic-lou',
      srcLabel: 'Menu.Download Approval Document',
      entryUrl: '/todo/download-lou?action=list',
      eventCountKey: 'downloadtheLOUCount',
    },
    {
      icon: 'ic-apply',
      srcLabel: 'Menu.Prepared Agreement Apply',
      entryUrl: '/todo/agreement-prep-list',
      eventCountKey: 'agreementPrepApplyCount',
    },
    {
      icon: 'ic-apply',
      srcLabel: 'Menu.Prepared Agreement Todo',
      entryUrl: '/todo/agreement-prep-todo-list',
      eventCountKey: 'agreementPrepTodoCount',
    },
    {
      icon: 'ic-sign-delivery-order',
      srcLabel: 'Menu.Sign Delivery Order',
      entryUrl: '/todo/sign-delivery-order-list',
      eventCountKey: 'signDeliveryOrderTodoCount',
    },
    {
      icon: 'ic-deal',
      srcLabel: 'Menu.Download Agreement',
      entryUrl: '/todo/download-agreement-list',
      eventCountKey: 'downloadAgreementCount',
    },
    {
      icon: 'ic-eh-apply',
      srcLabel: 'Menu.E-HAKMILIK Apply',
      entryUrl: '/todo/ehakmilik-apply-list',
      eventCountKey: 'ehakmilikApplyCount',
    },
    {
      icon: 'ic-file-down',
      srcLabel: 'Menu.E-HAKMILIK Download',
      entryUrl: '/todo/ehakmilik-download-list',
      eventCountKey: 'ehakmilikDownloadCount',
    },
    {
      icon: 'ic-hammer',
      srcLabel: 'Menu.Commencement Documents Upload',
      entryUrl: '/todo/commencement-doc-upload-list',
      eventCountKey: 'commencementDocUploadCount',
    },
  ];

  constructor(
    protected appConfig: AppConfig,
    private userProfileService: UserProfileService,
    private translateService: TranslateService,
    private http: HttpClient
  ) {}

  executeMenuProcessor(menus: MenuInfo[]): Observable<MenuInfo[]> {
    const observable = new Observable<MenuInfo[]>((observer) => {
      this.getWebSubmissionTodoCounts().subscribe({
        next: (result) => {
          const todoMenuItem = menus.find((x) =>
            x.data?.functionCode?.toLowerCase().startsWith('extodo')
          ) as TodoMenuInfo;
          if (todoMenuItem) {
            const todoSubMenus: MenuInfo[] = [];
            this.todoMenuInfos.forEach((menu) => {
              Object.keys(result).forEach((prop) => {
                if (prop === menu.eventCountKey && result[prop] > 0) {
                  const tmpMenu = deepCopy(menu);
                  (tmpMenu.data = {
                    ...todoMenuItem.data,
                    functionCode: tmpMenu.eventCountKey,
                    privilegeCode: tmpMenu.eventCountKey,
                    systemCode: SYSTEM_CODE,
                  }),
                  (tmpMenu.count = result[prop]);
                  tmpMenu.label = this.translateService.instant(
                    tmpMenu.srcLabel!
                  );
                  todoSubMenus.push(tmpMenu);
                }
              });
            });
            const todoCount = todoSubMenus.reduce((sum, menu: TodoMenuInfo) => {
              return sum + (menu.count ?? 0);
            }, 0);
            todoMenuItem.items = [
              ...(todoMenuItem.items ?? []),
              ...todoSubMenus,
            ];
            todoMenuItem.count = todoCount;
          }

          const insuranceMenuItem = menus.find(
            (x) => x.data?.functionCode?.toLowerCase() === 'exinsurance'
          ) as TodoMenuInfo;
          if (insuranceMenuItem) {
            insuranceMenuItem.count = result['insuranceCount'];
          }

          observer.next(menus);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
          observer.complete();
        },
      });
    });

    return observable;
  }

  private getWebSubmissionTodoCounts(): Observable<TodoListCountResponse> {
    const useRole = this.userProfileService.currentUserValue
      ?.extranetUserRole as any;
    const headers = new HttpHeaders().append('X-UseRole', useRole?.toString());
    const url = `${this.appConfig.getApiUrl('webSubmission')}/Common/TodoListCount`;
    return this.http.get<any>(url, { headers }).pipe(
      map((resp) => {
        if (resp && resp.rtnCode === 0 && resp.data) {
          return resp.data;
        }
        throw resp.msg;
      })
    );
  }
}

export interface WebSubmissionTodoMenuInfo extends TodoMenuInfo {
  eventCountKey?: string;
}

export interface TodoListCountResponse {
  [index: string]: any;
  fieldSettingDraftCount: number;
  fieldSettingApplyCount: number;
  fieldSettingApproveCount: number;
  fieldSettingWithdrawReviewCount: number;
  draftCount: number;
  submissionCount: number;
  rejectCount: number;
  announcememtApplyCount: number;
  announcementApproveCount: number;
}
